import { Label } from "@/components/ui/label";
import { Check } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface Option {
  value: string;
  label: string;
}

interface DropdownFieldProps {
  label: string;
  id: string;
  required?: boolean;
  value: string;
  options: Option[];
  validation: { isValid: boolean; error: string; touched: boolean };
  onChange: (value: string) => void;
  onBlur: () => void;
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
}

export const DropdownField = ({
  label,
  id,
  required = true,
  value,
  options,
  validation,
  onChange,
  onBlur,
  placeholder = "Select...",
  loading = false,
  disabled = false,
}: DropdownFieldProps) => (
  <div className="space-y-2.5">
    <Label htmlFor={id} className="text-base font-medium text-gray-700">
      {label}
      {required && " *"}
    </Label>
    <div className="relative">
      <Select
        value={value}
        onValueChange={(value) => {
          onChange(value);
          onBlur();
        }}
        disabled={disabled}
      >
        <SelectTrigger
          id={id}
          className={`h-12 text-base transition-all duration-200 focus:ring-2 ${
            disabled
              ? "bg-gray-100 cursor-not-allowed opacity-70"
              : validation.touched && validation.error
              ? "border-red-300 focus:ring-red-500/20 hover:border-red-400"
              : validation.touched && validation.isValid
              ? "border-green-300 focus:ring-green-500/20 hover:border-green-400"
              : "border-gray-200 focus:ring-teal-500/20 hover:border-gray-300"
          }`}
        >
          <SelectValue placeholder={<span className="text-gray-300">{placeholder}</span>} />
        </SelectTrigger>
        <SelectContent>
          {loading ? (
            <div className="p-2 text-center">
              <div className="h-4 w-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="text-sm text-gray-500 mt-2">Loading options...</p>
            </div>
          ) : options.length === 0 ? (
            <div className="p-2 text-center text-sm text-gray-500">No options available</div>
          ) : (
            options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
      {validation.touched && validation.isValid && !disabled && (
        <div className="absolute right-9 top-1/2 -translate-y-1/2">
          <Check className="h-5 w-5 text-green-500" />
        </div>
      )}
    </div>
    {validation.touched && validation.error && <p className="text-sm text-red-500">{validation.error}</p>}
  </div>
);
